import React from "react";
import { createRoot } from "react-dom/client";
// Typography uses each platform's native UI font stack (see SYSTEM_FONT in
// App.jsx) — nothing is downloaded, so no web-font request and no FOUT.
import App from "./App.jsx";
import { installStorageShim } from "./lib/storage.js";

// Must run before the app mounts: the app calls window.storage.* directly.
installStorageShim();

// Dump every yaadgrow:* key straight from localStorage as a JSON download.
// Independent of the app's own export so it still works when the app has crashed.
function downloadRawBackup() {
  try {
    const data = {};
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("yaadgrow:")) data[k] = localStorage.getItem(k);
    }
    const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), keys: data }, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `yaadgrow-recovery-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    // Nothing more we can safely do from the crash screen.
  }
}

// Catches render/runtime errors anywhere in the (single, large) component tree
// so one thrown error can't leave the user staring at a blank white page with
// no way to rescue their data.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the console for debugging; no external error reporting (the app
    // is privacy-local by design).
    console.error("YaadGrow crashed:", error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          boxSizing: "border-box",
          background: "linear-gradient(180deg, #031006 0%, #06180d 48%, #020b05 100%)",
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          color: "#e8f5e8",
        }}
      >
        <div
          style={{
            maxWidth: 440,
            width: "100%",
            background: "linear-gradient(180deg, #0d2010 0%, #0a1a0d 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 14,
            padding: 24,
            boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 8 }}>
            Something went wrong
          </div>
          <div style={{ fontSize: 13, color: "#8aaa8a", lineHeight: 1.6, marginBottom: 18 }}>
            YaadGrow hit an unexpected error and stopped. Your saved data is still
            on this device. Download a recovery copy, then reload to try again.
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={downloadRawBackup}
              style={{
                flex: 1,
                minWidth: 140,
                padding: "11px 14px",
                borderRadius: 9,
                border: "1px solid rgba(181,224,38,0.4)",
                background: "rgba(181,224,38,0.12)",
                color: "#b5e026",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Download recovery backup
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                flex: 1,
                minWidth: 120,
                padding: "11px 14px",
                borderRadius: 9,
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.06)",
                color: "#e8f5e8",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Reload app
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#5a7a5a", marginTop: 16, wordBreak: "break-word" }}>
            {String(this.state.error?.message || this.state.error || "Unknown error")}
          </div>
        </div>
      </div>
    );
  }
}

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
